import * as React from 'react';
import { apiFetch } from '@/lib/api';
import type { DealerTreeDealer } from '@/lib/admin-types';
import { stringifyError } from '@/lib/errors';

const DEALERS_TREE_URL = '/api/admin/dealers-tree?include_inactive_sites=1';
const BROADCAST_CHANNEL = 'incident-reporter:dealer-tree-invalidate';
const DEBOUNCE_MS = 250;
const MIN_REFRESH_INTERVAL_MS = 2_000;

export type DealerTreeContextValue = {
  dealers: DealerTreeDealer[];
  loading: boolean;
  error: string | null;
  treeRevision: number;
  reload: () => Promise<void>;
  invalidateDealerTree: (options?: { broadcast?: boolean }) => void;
};

const DealerTreeContext = React.createContext<DealerTreeContextValue | null>(null);

export function DealerTreeProvider({ children }: { children: React.ReactNode }) {
  const [dealers, setDealers] = React.useState<DealerTreeDealer[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [treeRevision, setTreeRevision] = React.useState(0);
  const activeRef = React.useRef(true);
  const pendingRequestsRef = React.useRef(new Set<AbortController>());

  const dealersRef = React.useRef(dealers);
  dealersRef.current = dealers;

  const load = React.useCallback(async (options?: { showLoading?: boolean }) => {
    if (!activeRef.current) return;
    const request = new AbortController();
    pendingRequestsRef.current.add(request);
    const showLoading = options?.showLoading ?? dealersRef.current.length === 0;
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<DealerTreeDealer[]>(DEALERS_TREE_URL, { signal: request.signal });
      if (request.signal.aborted) return;
      setDealers(Array.isArray(data) ? data : []);
      setTreeRevision((revision) => revision + 1);
    } catch (err) {
      if (!request.signal.aborted) setError(stringifyError(err));
    } finally {
      pendingRequestsRef.current.delete(request);
      if (showLoading && !request.signal.aborted) setLoading(false);
    }
  }, []);

  const loadRef = React.useRef(load);
  loadRef.current = load;

  const lastFlushMsRef = React.useRef(0);
  const debounceTimerRef = React.useRef<number | undefined>(undefined);

  const flushInvalidate = React.useCallback(() => {
    if (!activeRef.current) return;
    debounceTimerRef.current = undefined;
    const now = Date.now();
    if (now - lastFlushMsRef.current < MIN_REFRESH_INTERVAL_MS) {
      const waitMs = MIN_REFRESH_INTERVAL_MS - (now - lastFlushMsRef.current);
      debounceTimerRef.current = window.setTimeout(flushInvalidate, waitMs);
      return;
    }
    lastFlushMsRef.current = now;
    void loadRef.current({ showLoading: false });
  }, []);

  const invalidateDealerTree = React.useCallback((options?: { broadcast?: boolean }) => {
    if (!activeRef.current) return;
    if (options?.broadcast !== false && typeof BroadcastChannel !== 'undefined') {
      try {
        const channel = new BroadcastChannel(BROADCAST_CHANNEL);
        channel.postMessage({ type: 'invalidate' });
        channel.close();
      } catch {
        /* ignore */
      }
    }
    if (debounceTimerRef.current !== undefined) window.clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = window.setTimeout(flushInvalidate, DEBOUNCE_MS);
  }, [flushInvalidate]);

  React.useEffect(() => {
    activeRef.current = true;
    void loadRef.current({ showLoading: true });
    return () => {
      activeRef.current = false;
      for (const request of pendingRequestsRef.current) request.abort();
      pendingRequestsRef.current.clear();
      if (debounceTimerRef.current !== undefined) window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    };
  }, []);

  React.useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      invalidateDealerTree({ broadcast: false });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [invalidateDealerTree]);

  React.useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(BROADCAST_CHANNEL);
      channel.onmessage = () => {
        invalidateDealerTree({ broadcast: false });
      };
    } catch {
      return undefined;
    }
    return () => {
      channel?.close();
    };
  }, [invalidateDealerTree]);

  const value = React.useMemo<DealerTreeContextValue>(
    () => ({
      dealers,
      loading,
      error,
      treeRevision,
      reload: () => loadRef.current({ showLoading: dealersRef.current.length === 0 }),
      invalidateDealerTree,
    }),
    [dealers, loading, error, treeRevision, invalidateDealerTree],
  );

  return <DealerTreeContext.Provider value={value}>{children}</DealerTreeContext.Provider>;
}

export function useDealerTreeContext(): DealerTreeContextValue {
  const ctx = React.useContext(DealerTreeContext);
  if (!ctx) {
    throw new Error('useDealerTreeContext must be used within DealerTreeProvider');
  }
  return ctx;
}
